import { useState, useCallback } from 'react';
import type React from 'react';
import { Search, Package, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Input, Card, LoadingSpinner, Badge } from '@/components/ui';
import { pickupApi } from '@/services/api/pickupApi';
import toast from 'react-hot-toast';
import type { Order } from '@/types/models';
import type { QRScanResponse } from '@/services/api/pickupApi';
import { OrderStatusLabels } from '@/types/enums';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface ManualSearchProps {
  onOrderSelected: (scanResponse: QRScanResponse) => void;
  onClose: () => void;
}

const ManualSearch: React.FC<ManualSearchProps> = ({ onOrderSelected, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Order[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isVerifying, setIsVerifying] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setIsSearching(true);
    setHasSearched(true);
    try {
      const orders = await pickupApi.searchOrders(trimmed);
      setResults(orders);
      if (!orders.length) {
        toast.error('Заказы не найдены');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ошибка поиска'));
    } finally {
      setIsSearching(false);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectOrder = async (order: Order) => {
    setIsVerifying(order.id);
    try {
      // Simulate a scan for this order (manual lookup)
      // We create a QRScanResponse-like object from the order data
      const scanResponse: QRScanResponse = {
        order_id: order.id,
        order_number: order.order_number,
        status: order.status,
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        customer_email: null,
        shelf_location: order.shelf_location,
        message: `Заказ найден вручную — ${OrderStatusLabels[order.status]}`,
      };
      onOrderSelected(scanResponse);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ошибка при выборе заказа'));
    } finally {
      setIsVerifying(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-slate-400 via-blue-400 to-indigo-400" />

        <Card.Header className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Поиск заказа</h3>
            <p className="text-xs text-gray-500 mt-0.5">Введите номер заказа или имя клиента</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </Card.Header>

        <Card.Body className="space-y-4">
          {/* Search input */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Номер заказа, имя клиента..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleSearch}
              loading={isSearching}
              disabled={!query.trim()}
            >
              Найти
            </Button>
          </div>

          {/* Results */}
          <AnimatePresence mode="wait">
            {isSearching && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-8"
              >
                <LoadingSpinner />
              </motion.div>
            )}

            {!isSearching && results.length > 0 && (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="divide-y divide-gray-100 -mx-6"
              >
                {results.map((order, idx) => (
                  <motion.button
                    key={order.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleSelectOrder(order)}
                    disabled={isVerifying === order.id}
                    className="w-full flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors text-left group disabled:opacity-60"
                  >
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                      {isVerifying === order.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Package className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900">
                          №{order.order_number}
                        </span>
                        <Badge.Status status={order.status} />
                      </div>
                      {order.shelf_location && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          Полка: {order.shelf_location}
                        </p>
                      )}
                    </div>

                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                  </motion.button>
                ))}
              </motion.div>
            )}

            {!isSearching && hasSearched && results.length === 0 && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-8 text-center"
              >
                <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Заказы не найдены</p>
                <p className="text-xs text-gray-300 mt-1">Попробуйте другой запрос</p>
              </motion.div>
            )}
          </AnimatePresence>
        </Card.Body>
      </Card>
    </motion.div>
  );
};

export default ManualSearch;
